// DEPRECATED USING ImageDropZonePickerField FOR WEB
import { FontAwesome, MaterialIcons } from '@expo/vector-icons';
import { captureException } from '@sentry/react-native';
import { Image } from 'expo-image';
import * as ImageManipulator from 'expo-image-manipulator';
import * as ImagePicker from 'expo-image-picker';
import { useEffect, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import { Control, FieldValues, Path, useController } from 'react-hook-form';
import { Linking, StyleSheet, TouchableOpacity, useWindowDimensions, View } from 'react-native';
import { Modal, Portal, ProgressBar } from 'react-native-paper';

import { BLUR_HASH } from '@/constants/layout';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { useColor } from '@/hooks/useColor';
import { firebaseImage } from '@/utils/firebaseImage';
import { i18n } from '@/utils/i18n';

import BrandedButton from '../buttons/BrandedButton';
import CloseButton from '../buttons/CloseButton';
import DeleteButton from '../buttons/DeleteButton';
import DotsLoader from '../DotsLoader';
import Text from '../Text';

export type ImagePickerFieldProps<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  numberOfImages?: number;
  showError?: boolean;
  progress?: number;
  label?: string;
};

const ImagePickerField = <T extends FieldValues>({
  name,
  control,
  numberOfImages = 5,
  showError = true,
  progress,
  label
}: ImagePickerFieldProps<T>) => {
  const [showTypePicker, setShowTypePicker] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [currentImage, setCurrentImage] = useState<ImagePicker.ImagePickerResult | null>(null);
  const [imageFiles, setImageFiles] = useState<(string | undefined)[]>(new Array(numberOfImages).fill(undefined));
  const [indexToPick, setIndexToPick] = useState<number | null>(null);
  const [cropping, setCropping] = useState(false);

  const cropperRef = useRef<Cropper>(null);

  const { width, height } = useWindowDimensions();

  const {
    field: { value, onChange },
    fieldState: { error }
  } = useController<T>({
    name,
    control
  });

  const { showError: errorModal } = useEndFlowModal();
  const colors = useColor();

  const imgSize = (width > height ? height : width) - 100;

  //Locales
  const err_bigger_img_title = i18n.t('mix.err_bigger_img');
  const err_bigger_img_msg = i18n.t('mix.err_bigger_img_msg');
  const err_file_size_title = i18n.t('mix.err_getting_file_size');
  const err_taking_pic = i18n.t('mix.err_taking_img');
  const crop_img_title = i18n.t('mix.crop_img_modal_title');
  const done_btn = i18n.t('mix.done');
  const choose_from = i18n.t('mix.choose_from');
  const uploading = i18n.t('mix.uploading');
  const main = i18n.t('mix.main');
  const gallery = i18n.t('mix.gallery');
  const camera = i18n.t('mix.camera');

  useEffect(() => {
    if (Array.isArray(value)) {
      const mappedValue: (string | undefined)[] = value.map((photo: string | undefined) => {
        return photo ? firebaseImage(photo) : undefined;
      });

      const filler: (string | undefined)[] = new Array(numberOfImages - mappedValue.length).fill(undefined);
      mappedValue.push(...filler);
      setImageFiles(mappedValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCropFinished = async () => {
    if (!currentImage || !currentImage.assets || currentImage.assets.length === 0) {
      console.error('No selected image');
      return;
    }
    setCropping(true);

    const imageUri = currentImage.assets[0].uri;
    const cropData = cropperRef.current?.getCropData();
    const areaPixels = cropData?.croppedAreaPixels;
    const width = areaPixels?.width;
    const height = areaPixels?.height;
    const x = areaPixels?.x;
    const y = areaPixels?.y;

    const newFilesArray = [...imageFiles];
    await ImageManipulator.manipulateAsync(imageUri, [
      { crop: { width: width ?? 1000, height: height ?? 1000, originX: x ?? 0, originY: y ?? 0 } }
    ]).then((manipulatedImage) => {
      newFilesArray[indexToPick!] = manipulatedImage.uri;
    });

    try {
      const response = await fetch(newFilesArray[indexToPick!]!);
      const blob = await response.blob();

      const fileSize = blob.size / 1024 / 1024;

      if (2 >= fileSize) {
        onChange(newFilesArray);
        setImageFiles(newFilesArray);
      } else
        errorModal({
          title: err_bigger_img_title,
          message: err_bigger_img_msg
        });
    } catch (error) {
      errorModal({
        title: err_file_size_title
      });
      console.error('Error getting file size:', error);
      return;
    }

    setIndexToPick(null);
    setShowCropper(false);
    setCropping(false);
  };

  const pickImageFromLibrary = async () => {
    // No permissions request is necessary for launching the image library
    let result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: 'images',
      allowsEditing: true,
      aspect: [1, 1]
    });

    if (!result.canceled && indexToPick !== null) {
      setCurrentImage(result);
      setShowCropper(true);
    }
  };

  const launchCamera = async () => {
    let result = await ImagePicker.launchCameraAsync({
      mediaTypes: 'images',
      allowsEditing: true,
      aspect: [1, 1]
    });

    if (!result.canceled && indexToPick !== null) {
      setCurrentImage(result);
      setShowCropper(true);
    }
  };

  const pickImageFromCamera = async () => {
    try {
      const permissions = await ImagePicker.getCameraPermissionsAsync();

      if (!permissions?.granted && permissions?.canAskAgain) {
        ImagePicker.requestCameraPermissionsAsync().then(async (firstPermissions) => {
          if (firstPermissions.granted) await launchCamera();
        });
      } else if (permissions?.granted) {
        await launchCamera();
      } else {
        Linking.openSettings();
      }
    } catch (e: any) {
      captureException(e);
      errorModal({ title: err_taking_pic, message: e.message });
    }
  };

  const renderPickerContainers = ({
    imageSource,
    mainImage,
    index
  }: {
    imageSource: string | undefined | null;
    mainImage: boolean;
    index: number;
  }) => {
    return (
      <View key={index} style={[styles.pickerContainer, { flex: mainImage ? 1 : 1 / 2 }]}>
        {imageSource !== undefined && imageSource !== null ? (
          <TouchableOpacity
            style={styles.fullSize}
            onPress={() => {
              setShowTypePicker(true);
              setIndexToPick(index);
            }}>
            {mainImage && (
              <View style={styles.mainLabel}>
                <Text>{main}</Text>
              </View>
            )}
            <View style={mainImage ? styles.mainDeleteIcon : styles.deleteIcon}>
              <DeleteButton
                onPress={() => {
                  const newFilesArray = [...imageFiles];
                  newFilesArray[index] = undefined;

                  onChange(newFilesArray);
                  setImageFiles(newFilesArray);
                }}
              />
            </View>
            <Image
              source={imageSource}
              contentFit="cover"
              contentPosition={'center'}
              style={mainImage ? styles.mainImage : styles.smallImage}
              placeholder={{ blurhash: BLUR_HASH }}
            />
          </TouchableOpacity>
        ) : (
          <TouchableOpacity
            onPress={() => {
              setShowTypePicker(true);
              setIndexToPick(imageFiles.findIndex((it) => it === undefined));
            }}
            style={mainImage ? styles.mainPickerContainer : styles.smallPickerContainer}>
            {mainImage && (
              <View style={styles.mainLabel}>
                <Text>{main}</Text>
              </View>
            )}
            <FontAwesome name="plus" size={30} color="white" />
          </TouchableOpacity>
        )}
      </View>
    );
  };

  const smallImages = imageFiles.slice(1);

  return (
    <View style={styles.mainViewContainer}>
      <Portal>
        <Modal
          style={styles.modal}
          visible={showTypePicker}
          onDismiss={() => setShowTypePicker(false)}
          contentContainerStyle={styles.modalContent}>
          <View style={styles.modalView}>
            <CloseButton
              style={styles.closeBtn}
              onCloseTap={() => {
                setShowTypePicker(false);
              }}
            />
            <View style={styles.column}>
              <Text fontWeight="900" style={styles.selectTypeHeader}>
                {choose_from}
              </Text>
              <View style={styles.buttonsContainer}>
                <BrandedButton
                  style={[styles.flexOne, styles.horizontalPadding]}
                  textStyles={{ overflow: 'visible' }}
                  textProps={{ numberOfLines: 1 }}
                  onPress={() => {
                    setShowTypePicker(false);
                    pickImageFromLibrary();
                  }}>
                  <MaterialIcons name="add-photo-alternate" color="white" />
                  {` ${gallery}`}
                </BrandedButton>

                <BrandedButton
                  style={[styles.flexOne, styles.horizontalPadding]}
                  textStyles={{ overflow: 'visible' }}
                  textProps={{ numberOfLines: 1 }}
                  onPress={() => {
                    setShowTypePicker(false);
                    pickImageFromCamera();
                  }}>
                  <MaterialIcons name="add-a-photo" color="white" />
                  {` ${camera}`}
                </BrandedButton>
              </View>
            </View>
          </View>
        </Modal>
        <Modal visible={showCropper} onDismiss={() => setShowCropper(false)} style={styles.modal}>
          <View
            style={[
              {
                height: imgSize,
                width: imgSize
              },
              styles.cropImgContainer
            ]}>
            <View style={styles.cropImgHeader}>
              <Text style={[styles.selectTypeHeader, { color: colors.titleText }]} fontWeight="900">
                {crop_img_title}
              </Text>
              <CloseButton
                onCloseTap={() => {
                  setShowCropper(false);
                }}
              />
            </View>
            <View style={styles.cropperContainer}>
              {cropping && <View style={styles.croppingForegroundView} />}
              <Cropper
                ref={(node) => {
                  if (node) cropperRef.current = node;
                }}
                image={currentImage?.assets?.[0]?.uri ?? ''}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
              />
            </View>
            <BrandedButton onPress={onCropFinished} style={styles.cropBtn}>
              {cropping ? <DotsLoader color="white" /> : done_btn}
            </BrandedButton>
          </View>
        </Modal>
      </Portal>
      <View style={styles.imagesSelector}>
        {label && (
          <Text fontWeight="700" style={styles.label}>
            {label}
          </Text>
        )}
        {/* Fixed for 5 images */}
        <View style={styles.imagesContainer}>
          <View style={styles.mainImageContainer}>
            {renderPickerContainers({ imageSource: imageFiles[0], mainImage: true, index: 0 })}
          </View>

          <View style={styles.smallImagesContainer}>
            <View style={styles.smallImagesRow}>
              {renderPickerContainers({ imageSource: smallImages[0], mainImage: false, index: 1 })}
              {renderPickerContainers({ imageSource: smallImages[1], mainImage: false, index: 2 })}
            </View>
            <View style={styles.smallImagesRow}>
              {renderPickerContainers({ imageSource: smallImages[2], mainImage: false, index: 3 })}
              {renderPickerContainers({ imageSource: smallImages[3], mainImage: false, index: 4 })}
            </View>
          </View>
        </View>
      </View>

      {progress !== undefined && progress >= 0 && progress <= 100 && (
        <View style={styles.uploadingContainer}>
          <Text style={styles.uploadingLabel}>{uploading}</Text>
          <ProgressBar style={styles.progressBar} animatedValue={progress / 100} color="gold" />
        </View>
      )}

      {showError && !!error?.message && <Text style={styles.errorText}>{error.message}</Text>}
    </View>
  );
};

export default ImagePickerField;

const styles = StyleSheet.create({
  modal: {
    alignItems: 'center'
  },

  modalContent: {
    backgroundColor: 'white',
    padding: 20,
    borderRadius: 10
  },

  modalView: {
    paddingHorizontal: 10,
    flexDirection: 'column',
    borderRadius: 10
  },

  mainViewContainer: {
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center'
  },

  column: {
    flexDirection: 'column',
    rowGap: 20
  },

  horizontalPadding: {
    paddingHorizontal: 16
  },

  uploadingContainer: {
    flexDirection: 'column',
    height: 45,
    width: '100%'
  },

  uploadingLabel: {
    width: '100%',
    color: 'black',
    marginTop: 10
  },

  progressBar: {
    marginTop: 10,
    backgroundColor: 'gray'
  },

  selectTypeHeader: {
    fontSize: 22,
    color: 'black',
    justifyContent: 'center',
    alignSelf: 'center',
    textAlign: 'center'
  },

  buttonsContainer: {
    flexDirection: 'row',
    gap: 15
  },

  errorText: {
    marginTop: 10,
    fontSize: 12,
    color: 'red'
  },

  mainDeleteIcon: {
    position: 'absolute',
    top: 10,
    right: 25,
    backgroundColor: 'white',
    paddingHorizontal: 2,
    paddingVertical: 1,
    borderRadius: 10,
    zIndex: 10
  },

  deleteIcon: {
    position: 'absolute',
    top: 10,
    right: 5,
    backgroundColor: 'white',
    paddingHorizontal: 2,
    paddingVertical: 1,
    borderRadius: 10,
    zIndex: 10
  },

  mainLabel: {
    position: 'absolute',
    bottom: 10,
    left: 5,
    backgroundColor: 'white',
    paddingHorizontal: 10,
    paddingVertical: 1,
    borderRadius: 10,
    zIndex: 10
  },

  pickerContainer: {
    width: '100%',
    height: '100%',
    aspectRatio: 1
  },

  mainPickerContainer: {
    width: '100%',
    height: '100%',
    backgroundColor: '#D9D9D9',
    justifyContent: 'center',
    alignItems: 'center'
  },

  smallPickerContainer: {
    width: '100%',
    height: '100%',
    backgroundColor: '#D9D9D9',
    justifyContent: 'center',
    alignItems: 'center'
  },

  mainImage: {
    width: '100%',
    height: '100%'
  },

  smallImage: {
    width: '100%',
    height: '100%'
  },

  imagesSelector: {
    width: '100%',
    height: '100%',
    flexDirection: 'column'
  },

  imagesContainer: {
    flexDirection: 'row',
    width: '100%',
    height: '100%',
    gap: 10
  },

  flexOne: {
    flex: 1
  },

  mainImageContainer: {
    flex: 1 / 2,
    width: '100%',
    height: '100%'
  },

  smallImagesContainer: {
    flex: 1 / 2,
    width: '100%',
    height: '100%',
    gap: 10
  },

  smallImagesRow: {
    flex: 1 / 2,
    flexDirection: 'row',
    gap: 10
  },

  label: {
    color: 'black',
    marginBottom: 10
  },

  fullSize: {
    width: '100%',
    height: '100%'
  },

  cropImgContainer: {
    backgroundColor: 'white',
    borderRadius: 10,
    padding: 20,
    display: 'flex',
    flexDirection: 'column',
    rowGap: 20
  },

  cropImgHeader: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },

  cropperContainer: {
    flex: 1,
    borderRadius: 10,
    overflow: 'hidden'
  },

  cropBtn: {
    paddingHorizontal: 10
  },

  closeBtn: {
    alignSelf: 'flex-start'
  },

  croppingForegroundView: {
    backgroundColor: 'rgba(255,255,255,0.5)',
    flex: 1,
    zIndex: 1
  }
});
